import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createWorksheet, createRole, getWorksheetDetail, updateRole, deleteRole, deleteWorksheet, updateWorksheet, printWorksheet } from "../api";
import { useNavigate } from "react-router-dom";
import { useNotification } from "../../../contexts/NotificationContext";


export const useGetWorksheetDetail = (id) => {
  const { data, isLoading, isError, error, isSuccess } = useQuery({
    queryKey: ['worksheet', id],
    queryFn: () => getWorksheetDetail(id),
    enabled: !!id,
    staleTime: 1000 * 60 * 5,
    cacheTime: 1000 * 60 * 10,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });

  return { data, isLoading, isSuccess, isError, error };
};

export const useCreateWorksheet = () => {
  const queryClient = useQueryClient();
  const { showNotification } = useNotification();

  const mutation = useMutation({
    mutationFn: (data) => createWorksheet(data),
    onSuccess: async (_value, variables) => {
      await queryClient.invalidateQueries({ queryKey: ["deal", variables.deal_id, "WORKSHEETS"] });


      showNotification('success', 'Worksheet created successfully!');

    },
    onError: (error) => {
      let errorMessage = 'Error creating worksheet. Please try again.';
      if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      console.error("Error creating worksheet:", error);
    },
  });

  return mutation;
};

export const useUpdateWorksheet = (options = {}) => {
  const queryClient = useQueryClient();
  const { showNotification } = useNotification();

  const mutation = useMutation({
    mutationFn: ({ id, data }) => updateWorksheet(id, data),
    onSuccess: async (_value, variables) => {

      await queryClient.invalidateQueries({ queryKey: ["deal", variables.dealId, "WORKSHEETS"] });
      await queryClient.invalidateQueries({ queryKey: ["worksheet"] });

      showNotification('success', 'Worksheet updated successfully!');

      // Call custom onSuccess if provided
      if (options.onSuccess) {
        options.onSuccess(_value, variables);
      }
    },
    onError: (error) => {
      console.error("Error updating role:", error);

      let errorMessage = 'Error updating worksheet. Please try again.';
      if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      showNotification('danger', errorMessage);

      // Call custom onError if provided
      if (options.onError) {
        options.onError(error);
      }
    },
  });

  return mutation;
};

export const useDeleteWorksheet = () => {
  const queryClient = useQueryClient();
  const { showNotification } = useNotification();

  return useMutation({
    mutationFn: ({ id }) => deleteWorksheet(id),
    onSuccess: (_data, variables) => {
      // Invalidate the deal detail (worksheets tab) so the list refreshes
      if (variables?.dealId) {
        queryClient.invalidateQueries({ queryKey: ["deal", variables.dealId, "WORKSHEETS"] });
        queryClient.invalidateQueries({ queryKey: ["deal", variables.dealId] });
      }
      queryClient.invalidateQueries({ queryKey: ["worksheet"] });
      showNotification('success', 'Worksheet deleted successfully!');
    },
    onError: (error) => {
      console.error("Error deleting role:", error);

      let errorMessage = 'Error deleting worksheet. Please try again.';
      if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      showNotification('danger', errorMessage);
    },
  });
};

export const usePrintWorksheet = () => {
  const { showNotification } = useNotification();

  return useMutation({
    mutationFn: ({ id, isWorksheet, documents, documentName }) => printWorksheet(id, isWorksheet, documents, documentName),
    onSuccess: (response) => {
      showNotification('success', 'PDF loaded successfully!');
      return response;
    },
    onError: (error) => {
      console.error("Error printing worksheet:", error);
      showNotification('danger', 'Error generating PDF. Please try again.');
    },
  });
};